/**
 * TutorApplicationInterviewScreen — un entretien de la candidature (route `application-interview`,
 * l'ancien `InterviewScreen`), ouvert par l'intro du champion de l'étape avec sa catégorie
 * (`testCategory` : `motivation_`, `educational_`, ou `<catégorie>_video`) et son rang (`stepNum`).
 *
 *  - écrit (motivation, pédagogie) : les questions une à la fois, puis la fin d'étape ;
 *  - vidéo : la question servie, sa réflexion, la caméra, l'envoi, la vidéo à revoir, la fin d'étape.
 */
import React from "react"

import { Snackbar } from "../../../../../../design-system"
import {
    TutorApplicationQuestionPage,
    TutorApplicationStepDonePage,
    TutorApplicationVideoQuestionPage,
} from "../../../../../../design-system/tutor"
import { type TutorSpaceScreenProps } from "../tutorSpaceRouteTypes"
import { interviewParams } from "./interviewParams"
import { useTutorApplicationVideoQuestionState } from "./useTutorApplicationVideoQuestionState"
import { useTutorApplicationWrittenState } from "./useTutorApplicationWrittenState"

export function TutorApplicationInterviewScreen(props: TutorSpaceScreenProps) {
    const params = interviewParams(props.route?.params)
    return params.video ? <VideoInterview {...params} /> : <WrittenInterview {...params} />
}

type Params = ReturnType<typeof interviewParams>

function WrittenInterview(props: Params) {
    const form = useTutorApplicationWrittenState(props.category, props.stepNum)

    if (form.done) return <TutorApplicationStepDonePage {...form.stepDone} testID="tutor-application-step-done" />
    return (
        <>
            {form.page != null ? (
                <TutorApplicationQuestionPage {...form.page} testID="tutor-application-interview" />
            ) : (
                // Aucune question pour cette catégorie : l'ancien écran disait « déjà vue ».
                <TutorApplicationVideoQuestionPage {...form.empty} testID="tutor-application-interview" />
            )}
            <Snackbar {...form.restoredNotice} />
        </>
    )
}

function VideoInterview(props: Params) {
    const video = useTutorApplicationVideoQuestionState(props.category, props.stepNum)

    if (video.done) return <TutorApplicationStepDonePage {...video.stepDone} testID="tutor-application-step-done" />
    return <TutorApplicationVideoQuestionPage {...video.page} testID="tutor-application-video" />
}
